/**
 * @graphql agg
 */
import gql from 'graphql-tag';
import { useCallback, useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import OriginTracer from '@atlassiansox/origin-tracing';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	ConfluenceDatabaseAri,
	ConfluencePageAri,
	ConfluenceBlogpostAri,
	ConfluenceWhiteboardAri,
	ConfluenceFolderAri,
	ConfluenceWorkspaceAri,
} from '@atlassian/ari/confluence';

import { generatePathWithFeatureCodesQueryParam } from '@confluence/feature-codes/entry-points/featureCodes.ts';
import { FeatureCode } from '@confluence/feature-codes/entry-points/FeatureCodeModel.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { usePageSpaceKey, useContentType } from '@confluence/page-context';
import type { RoutesContextType } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import {
	addQueryParams,
	removeEverythingAfterContentSlug,
} from '@confluence/route-manager-utils/entry-points/url.ts';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { VIEW_BLOG, VIEW_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { isEditPage } from '@confluence/named-routes/entry-points/routes-utils.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getConfluenceContentAri } from '@confluence/content-types-utils/entry-points/getConfluenceContentAri.ts';
import { BadStatusError, cfetch } from '@confluence/network';
import { useCopyContentLink } from '@confluence/copy-content-link/entry-points/useCopyContentLink.ts';

// eslint-disable-next-line import/no-cycle
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../Controller.tsx';

import type {
	useShareToPrincipalsShareToPrincipalsContentQuery as useShareToPrincipalsShareToPrincipalsContentQuery$data,
	useShareToPrincipalsShareToPrincipalsContentQueryVariables as useShareToPrincipalsShareToPrincipalsContentQuery$variables,
} from './__types__/useShareToPrincipalsShareToPrincipalsContentQuery.ts';
import type { NotificationRecipient } from './notificationsTransformer.ts';

const useShareToPrincipalsShareToPrincipalsContentQuery = gql`
	query useShareToPrincipalsShareToPrincipalsContentQuery($contentId: ID!) {
		singleContent(id: $contentId) {
			title
			type
			id
		}
	}
`;
const SUPPORTED_CONTENT_TYPES = ['page', 'blogpost', 'whiteboard', 'database', 'embed', 'folder'];

const i18n = defineMessages({
	emptyTitleDefault: {
		id: 'share-and-restrict-dialog.default.title',
		description: 'Default share title for content that is untitled',
		defaultMessage: 'Untitled',
	},
});

//TODO: This was borrowed from contentTypeHelper.ts in the old share package
//Please revisit during GA
export const getContentAri = ({
	activationId,
	cloudId,
	contentId,
	contentType,
}: {
	activationId: string;
	cloudId: string;
	contentId: string;
	contentType: string;
}) => {
	// Build ARI for embeds via `getConfluenceContentAri` until support is added to `@atlassian/ari`
	// TODO: Replace with `@atlassian/ari` https://hello.jira.atlassian.cloud/browse/CTIA-1835
	if (contentType === 'embed') {
		return getConfluenceContentAri({
			siteId: cloudId,
			resourceType: contentType,
			activationId,
			resourceId: contentId,
		});
	}

	if (contentType === 'database') {
		return ConfluenceDatabaseAri.create({
			siteId: cloudId,
			databaseId: contentId,
			activationId,
		}).toString();
	}

	let ari:
		| ConfluencePageAri
		| ConfluenceWhiteboardAri
		| ConfluenceBlogpostAri
		| ConfluenceFolderAri
		| null = null;
	// A space ari should not have spacekey as it is UGC (HOT-96075)
	// A space should have a page based ARI as only then it will generate the
	// notification that will be propagated to mobile as well and will be consistent with Mobile.
	// So a space will also have page based ARI. (Discussed in HOT-96778)
	switch (contentType) {
		case 'blogpost':
			ari = ConfluenceBlogpostAri.create({
				siteId: cloudId,
				blogpostId: contentId,
			});
			break;
		case 'whiteboard':
			ari = ConfluenceWhiteboardAri.create({
				siteId: cloudId,
				whiteboardId: contentId,
			});
			break;
		case 'folder':
			ari = ConfluenceFolderAri.create({
				siteId: cloudId,
				folderId: contentId,
				activationId,
			});
			break;
		default:
			ari = ConfluencePageAri.create({
				siteId: cloudId,
				pageId: contentId,
			});
			break;
	}

	return ari.toString();
};

export const getContentShareLink = ({
	routesContext,
	contentType,
	contentId,
	spaceKey,
	isEditMode,
	makeContentLink,
}: {
	routesContext: RoutesContextType;
	contentType: string;
	contentId: string;
	spaceKey?: string;
	isEditMode: boolean;
	makeContentLink: () => string | undefined;
}): string => {
	const draftShareId = getSingleParam(routesContext.getQueryParams(), 'draftShareId');
	/**
	 * Use the refactored version if in edit mode (for presence feature).
	 * If `makeContentLink` returns undefined, fall back to the original logic.
	 */
	const contentShareLink = makeContentLink();

	if (isEditMode && contentShareLink) {
		return contentShareLink;
	}

	/**
	 * Generate the "view content" link based on the current route context.
	 * If the user is on an edit page (excluding drafts), generate a "view" URL.
	 */
	const generateViewLink = () => {
		// ShareButton constructs the URL to send based on routes context (current
		// location in Confluence app). We are matching ShareButton logic as closely
		// as we can to provide a consistent experience, but given that we could
		// embed Share anywhere in the app, we should consider constructing URLs
		// based on a combination of content data from the BE (eg fetch content
		// type, status etc) + consumer specifications (give me a view vs edit URL)
		// and maintain an explicit set of link generation rules in this package.
		const namedRoute = routesContext.match;
		// If user is coming from edit page/blog, we want to send a "view content"
		// link, NOT the current edit URL. (Drafts are an exception; they have
		// no valid view page URL so we must construct a separate link.
		if (namedRoute && isEditPage(String(namedRoute)) && !draftShareId) {
			const spaceKeyParam = spaceKey ?? namedRoute?.params.spaceKey;
			const viewRoute = contentType === 'blog' ? VIEW_BLOG : VIEW_PAGE;

			return new URL(
				viewRoute.toUrl({ spaceKey: spaceKeyParam, contentId }),
				document.baseURI,
			).toString();
		}
		return null;
	};

	const viewLink = generateViewLink();
	if (viewLink) return viewLink;

	// At this point we know we want to share content in the VIEW mode + double checks that getCopyContentLink didn't return undefined
	if (contentShareLink) {
		return contentShareLink;
	}

	/**
	 * If no custom link or view link is available, fall back to the current URL
	 * with the content slug and possibly add a draft share ID as a query parameter.
	 */
	// eslint-disable-next-line no-restricted-syntax
	const fallbackLink = removeEverythingAfterContentSlug(window.location.href);
	return draftShareId ? addQueryParams(fallbackLink, { draftShareId }) : fallbackLink;
};

export const useShareToPrincipals = (
	contentId: string,
): {
	shareToPrincipals: ({
		notificationRecipients,
		message,
	}: {
		notificationRecipients: NotificationRecipient[];
		message?: string;
	}) => Promise<void>;
} => {
	const SHARE_ENDPOINT = '/gateway/api/contentShare';
	// Query the backend as the source of truth for content type,
	// with page context (routes-based) as a fallback
	const { data, error } = useQuery<
		useShareToPrincipalsShareToPrincipalsContentQuery$data,
		useShareToPrincipalsShareToPrincipalsContentQuery$variables
	>(useShareToPrincipalsShareToPrincipalsContentQuery, {
		variables: { contentId },
	});
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { activationId, cloudId } = useSessionData();
	const [contentTypeFromPageContext] = useContentType();
	const [spaceKey] = usePageSpaceKey();
	// eslint-disable-next-line no-restricted-syntax
	const routesContext = useContext(RoutesContext);
	const namedRoute = routesContext.match;
	const isEditMode = isEditPage(namedRoute?.name ?? '');

	// Determine content type from query data or fallback to page context
	const contentType = data?.singleContent?.type ?? contentTypeFromPageContext ?? '';
	const contentTitle = data?.singleContent?.title;

	const originTracingFactory = useCallback(() => new OriginTracer({ product: 'confluence' }), []);

	const { makeContentLink } = useCopyContentLink({
		contentType,
		pageMode: isEditMode ? 'edit' : 'view',
		contentId,
		spaceKey: spaceKey || '',
		source: 'useShareToPrincipals',
	});

	// Handle potential errors silently; missing data won't significantly impact users
	if (error) {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'errored',
				actionSubject: 'shareToPrincipalsContentQuery',
				actionSubjectId: 'useShareToPrincipals',
				attributes: {
					message: error.message,
				},
			},
		}).fire();
		markErrorAsHandled(error);
	}

	return {
		shareToPrincipals: async ({ notificationRecipients, message }) => {
			throwError(contentId, contentType);

			try {
				const requestBody = {
					content: {
						link: generatePathWithFeatureCodesQueryParam(
							getContentShareLink({
								routesContext,
								contentType,
								contentId,
								spaceKey,
								isEditMode,
								makeContentLink,
							}),
							[FeatureCode.SHARE_AND_RESTRICT_DIALOG],
						),
						ari: getContentAri({ activationId, contentId, cloudId, contentType }),
						workspaceAri: ConfluenceWorkspaceAri.create({
							siteId: cloudId,
							activationId,
						}).toString(),
						title: contentTitle || intl.formatMessage(i18n.emptyTitleDefault),
						type: contentType,
					},
					recipients: notificationRecipients,
					comment: {
						format: 'plain_text',
						value: message || '',
					},
					metadata: {
						productId: 'confluence',
						atlOriginId: String(originTracingFactory().id),
						// even if we are in edit mode we want to share the "view" page URL, not edit
						shareeAction: isEditMode ? 'edit' : 'view',
					},
				};

				await cfetch(SHARE_ENDPOINT, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json; charset=UTF-8',
					},
					body: JSON.stringify(requestBody),
				});
			} catch (err) {
				const response = BadStatusError.isBadStatusError(err) ? err.response : undefined;
				if (response) {
					const errDetails = await err.response.json();
					const isListTooLargeError = errDetails?.messagesDetails.some(
						(errObj) => errObj?.errorCode === 'share-to-large-group-forbidden',
					);
					if (isListTooLargeError) {
						throw new Error('list-too-large-error');
					}
				}
				throw new Error(err);
			}
		},
	};
};

function throwError(contentId: string, contentType: string) {
	if (!SUPPORTED_CONTENT_TYPES.includes(contentType)) {
		throw new Error('Not a supported content type');
	}

	if (!contentId) {
		throw new Error('No content ID');
	}
}
